type GtagCommand = 'config' | 'event' | 'js' | 'set';

interface GtagConfigParams {
  send_page_view?: boolean;
  page_path?: string;
  page_title?: string;
  [key: string]: unknown;
}

interface GtagEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  page_path?: string;
  button_label?: string;
  form_name?: string;
  location?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    gtag: (
      command: GtagCommand,
      targetIdOrEventName: string,
      params?: GtagConfigParams | GtagEventParams
    ) => void;
    dataLayer: unknown[];
    lintrk: {
      (command: 'track', params: { conversion_id: number }): void;
      q?: unknown[];
    };
    _linkedin_data_partner_ids: string[];
  }
}

const GA_MEASUREMENT_ID = 'G-GMMX61L83S';
const LINKEDIN_PARTNER_ID = '9354458';
const LINKEDIN_CONVERSION_ID_BOOK_A_DEMO = 28683378;
const CONSENT_KEY = 'smcla_analytics_consent';

export function hasAnalyticsConsent(): boolean {
  return localStorage.getItem(CONSENT_KEY) === 'true';
}

function isGtagAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    hasAnalyticsConsent()
  );
}

function loadGtag(): void {
  if (document.getElementById('gtag-script')) return;
  const s = document.createElement('script');
  s.id = 'gtag-script';
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments as unknown); };
  window.gtag('js', new Date() as unknown as string);
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

function loadLinkedInInsightTag(): void {
  if (document.getElementById('li-insight-tag')) return;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  if (!window.lintrk) {
    // eslint-disable-next-line prefer-rest-params
    window.lintrk = function lintrk() { (window.lintrk.q = window.lintrk.q || []).push(arguments as unknown as unknown[]); } as Window['lintrk'];
    window.lintrk.q = [];
  }
  const s = document.createElement('script');
  s.id = 'li-insight-tag';
  s.async = true;
  s.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  document.head.appendChild(s);
}

// Auto-load on import if user previously accepted
if (typeof window !== 'undefined' && hasAnalyticsConsent()) {
  loadGtag();
  loadLinkedInInsightTag();
}

export function grantAnalyticsConsent(currentPath: string): void {
  localStorage.setItem(CONSENT_KEY, 'true');
  loadGtag();
  loadLinkedInInsightTag();
  setTimeout(() => trackPageView(currentPath), 200);
}

export function trackLinkedInConversion(conversionId: number): void {
  if (!hasAnalyticsConsent() || typeof window.lintrk !== 'function') return;
  window.lintrk('track', { conversion_id: conversionId });
}

export function denyAnalyticsConsent(): void {
  localStorage.setItem(CONSENT_KEY, 'false');
}

export function trackPageView(path: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('config', GA_MEASUREMENT_ID, { page_path: path });
}

export function trackEvent(
  eventName: string,
  buttonLabel: string,
  pagePath: string,
  extraParams?: GtagEventParams
): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', eventName, {
    event_category: 'engagement',
    button_label: buttonLabel,
    page_path: pagePath,
    ...extraParams,
  });
}

export function trackFormSubmission(pagePath: string): void {
  trackLinkedInConversion(LINKEDIN_CONVERSION_ID_BOOK_A_DEMO);
  if (!isGtagAvailable()) return;
  window.gtag('event', 'generate_lead', {
    event_category: 'conversion',
    form_name: 'book_a_demo',
    page_path: pagePath,
  });
}

export function trackFormView(pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'form_view', {
    event_category: 'form_funnel',
    page_path: pagePath,
    button_label: 'form_view',
  });
}

export function trackFormStart(pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'form_start', {
    event_category: 'form_funnel',
    page_path: pagePath,
    button_label: 'form_start',
  });
}

export function trackFormAttempt(pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'form_attempt', {
    event_category: 'form_funnel',
    page_path: pagePath,
    button_label: 'form_attempt',
  });
}

export function trackFormError(pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'form_error', {
    event_category: 'form_funnel',
    page_path: pagePath,
    button_label: 'form_error',
  });
}

export function trackUtmEntry(pagePath: string): void {
  if (!isGtagAvailable()) return;
  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');
  const utm_content = params.get('utm_content');
  const utm_term = params.get('utm_term');
  if (!utm_source && !utm_campaign) return;
  window.gtag('event', 'lp_entry', {
    event_category: 'campaign',
    page_path: pagePath,
    utm_source: utm_source ?? '(none)',
    utm_medium: utm_medium ?? '(none)',
    utm_campaign: utm_campaign ?? '(none)',
    utm_content: utm_content ?? '(none)',
    utm_term: utm_term ?? '(none)',
  });
}

// ── Video tracking ─────────────────────────────────────────────────────────────
export function trackVideoPlay(videoName: string, pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'video_play', {
    event_category: 'video',
    video_name: videoName,
    page_path: pagePath,
  });
}

export function trackVideoMilestone(videoName: string, milestonePct: number, pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'video_milestone', {
    event_category: 'video',
    video_name: videoName,
    milestone_pct: milestonePct,
    page_path: pagePath,
  });
}

export function trackVideoComplete(videoName: string, pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'video_complete', {
    event_category: 'video',
    video_name: videoName,
    page_path: pagePath,
  });
}

// ── Section view ───────────────────────────────────────────────────────────────
export function trackKeySection(sectionName: string, pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'key_section_view', {
    event_category: 'engagement',
    section_name: sectionName,
    page_path: pagePath,
  });
}

// ── Form abandon ───────────────────────────────────────────────────────────────
export function trackFormAbandon(pagePath: string, fieldsFilled: number): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'form_abandon', {
    event_category: 'form_funnel',
    page_path: pagePath,
    fields_filled: fieldsFilled,
  });
}

// ── Email click ────────────────────────────────────────────────────────────────
export function trackEmailClick(pagePath: string, location: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'email_click', {
    event_category: 'engagement',
    page_path: pagePath,
    location,
  });
}

// ── Navigation intent ──────────────────────────────────────────────────────────
export function trackNavigationIntent(fromPath: string, toPath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'navigation_intent', {
    event_category: 'engagement',
    from_page: fromPath,
    to_page: toPath,
  });
}

// ── Return visit ───────────────────────────────────────────────────────────────
export function trackReturnVisit(pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'return_visit', {
    event_category: 'engagement',
    page_path: pagePath,
  });
}

// ── Persona + visitor type custom dimensions ──────────────────────────────────
export function setPersonaDimension(pagePath: string): void {
  if (!isGtagAvailable()) return;
  const match = pagePath.match(/^\/lp\/([^/?]+)/);
  if (!match) return;
  window.gtag('set', 'user_properties', { persona: match[1] });
}

export function setVisitorTypeDimension(): void {
  if (typeof window === 'undefined') return;
  const key = 'smcla_visited';
  const isReturning = !!localStorage.getItem(key);
  localStorage.setItem(key, Date.now().toString());
  if (!isGtagAvailable()) return;
  window.gtag('set', 'user_properties', { visitor_type: isReturning ? 'returning' : 'new' });
}

export function incrementSessionDepth(): void {
  if (typeof window === 'undefined') return;
  const key = 'smcla_session_depth';
  const depth = parseInt(sessionStorage.getItem(key) ?? '0', 10) + 1;
  sessionStorage.setItem(key, depth.toString());
  if (!isGtagAvailable()) return;
  window.gtag('set', 'user_properties', { session_depth: depth });
}

export function trackScrollDepth(milestone: number, pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'scroll_milestone', {
    event_category: 'engagement',
    page_path: pagePath,
    milestone_pct: milestone,
    button_label: `${milestone}%`,
  });
}

export function trackTimeOnPage(seconds: number, pagePath: string): void {
  if (!isGtagAvailable()) return;
  window.gtag('event', 'time_on_page', {
    event_category: 'engagement',
    page_path: pagePath,
    seconds_on_page: seconds,
    button_label: `${seconds}s`,
  });
}
